import { Divider, Stack } from '@mui/material';
import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { RoiId } from '~/domain/changes/ids';
import {
    domainEventBus,
    RoiVisibilityChangeRequestedEvent,
} from '~/domain/events';
import {
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import useViewportFocus from '~/hooks/useViewportFocus';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import StructureSet from '../../ContouringStep/StructureSet/StructureSet';
import ReviewControls from './ReviewControls';
import ReviewPlayer from './ReviewPlayer';

export default function ReviewStructureSet({
    structureSet,
}: {
    structureSet: StructureSetReadModel;
}) {
    const selectedROINumber = useRecoilValue(selectedROIState);
    const { focusOnStructure } = useViewportFocus();

    // Capture initial visibility so we can restore it when leaving review mode
    // or switching to a different structure set. The snapshot is re-taken
    // whenever structureSet.dicomUid changes; the cleanup restores it.
    useEffect(() => {
        const capturedVisibility = new Map(
            structureSet.roiArray.map((r) => [r.roiNumber, r.visible])
        );
        return () => {
            capturedVisibility.forEach((wasVisible, roiNumber) => {
                domainEventBus.publish(
                    new RoiVisibilityChangeRequestedEvent(
                        roiNumber,
                        wasVisible,
                        'user'
                    )
                );
            });
        };
        // structureSet.roiArray intentionally omitted — we only re-capture
        // the snapshot when the structure set itself changes, not on every
        // individual visibility update.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // When the selected ROI changes, hide all other ROIs, make the selected
    // one visible, and auto-focus the viewport.
    useEffect(() => {
        if (selectedROINumber === -1) {
            return;
        }
        structureSet.roiArray.forEach((roi) => {
            domainEventBus.publish(
                new RoiVisibilityChangeRequestedEvent(
                    roi.roiNumber,
                    roi.roiNumber === selectedROINumber,
                    'user'
                )
            );
        });
        focusOnStructure(selectedROINumber);
        // focusOnStructure intentionally omitted
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedROINumber, structureSet.dicomUid]);

    const renderReviewExtras = (
        roi: RoiReadModel,
        roiId: RoiId | undefined
    ) => (
        <Stack spacing={1.5} mt={2}>
            <Divider />
            <ReviewPlayer roi={roi} showLabel={false} />
            <ReviewControls roi={roi} roiId={roiId} />
        </Stack>
    );

    return (
        <StructureSet
            structureSet={structureSet}
            renderExtraDetails={renderReviewExtras}
        />
    );
}
